<template>
  <div class="flex flex-column gap-4">
    <div class="flex justify-content-between align-items-start gap-3">
      <div>
        <h3 class="m-0">Employment Lifecycle</h3>
        <span class="profile-lifecycle-subtitle">Every step of the associate's time here - from joining to today.</span>
      </div>
    </div>

    <a-spin :spinning="loading || dynamicFormLoading">
      <div v-if="!loading && !associateLifecycleEvents" class="empty-flow">Unable to load lifecycle events.</div>
      <div v-else-if="!loading && !hasLifecycleEvents" class="empty-flow">No lifecycle events.</div>
      <profile-lifecycle-periods
        v-else
        :lifecycle="lifecyclePeriods"
        :company-flow="lifecycleCompanyFlow"
        :dynamic-form-availability="lifecycleDynamicFormAvailability"
        :signed-documents="signedDocuments.items ?? []"
        :first-shift-date="lifecycleFirstShiftDate"
        :seniority-date="lifecycleSeniorityDate"
        @save="loadLifecycleEvents"
      />
    </a-spin>
  </div>
</template>

<script>
import { Button, Dropdown, Icon, Menu, notification, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import LifecyclePeriods from './lifecycle-periods.vue'

const { mapState: profileState, mapActions: profileActions } = createNamespacedHelpers('profile')
const { mapState: documentState } = createNamespacedHelpers('document')

export default {
  name: 'ProfileViewLifecycle',
  components: {
    'a-button': Button,
    'a-dropdown': Dropdown,
    'a-icon': Icon,
    'a-menu': Menu,
    'a-menu-item': Menu.Item,
    'a-spin': Spin,
    'profile-lifecycle-periods': LifecyclePeriods
  },
  data() {
    return {
      loading: false
    }
  },
  props: {
    dynamicFormLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate,
      associateCompanyFlow: state => state.associateCompanyFlow,
      associateLifecycleEvents: state => state.associateLifecycleEvents,
      signedDocuments: state => state.signedDocuments
    }),
    ...documentState({
      associateDynamicFormAvailability: state => state.associateDynamicFormAvailability
    }),

    hasLifecycleEvents() {
      return Boolean(this.lifecyclePeriods?.length)
    },

    lifecyclePeriods() {
      return this.associateLifecycleEvents?.lifecycle
    },

    lifecycleCompanyFlow() {
      return this.associateCompanyFlow
    },

    lifecycleDynamicFormAvailability() {
      return this.dynamicFormLoading ? {} : this.associateDynamicFormAvailability || {}
    },

    lifecycleFirstShiftDate() {
      return this.associateLifecycleEvents?.first_shift_date
    },

    lifecycleSeniorityDate() {
      return this.associateLifecycleEvents?.seniority_date
    }
  },
  watch: {
    'associate.id': {
      handler(newVal) {
        if (newVal) {
          this.loadLifecycleEvents()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...profileActions([
      'getAssociateCompanyFlow',
      'getAssociateLifecycleEvents',
      'getSignedDocuments',
      'setAssociateLifecycleEvents'
    ]),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async loadLifecycleEvents() {
      this.loading = true

      try {
        this.setAssociateLifecycleEvents(undefined)
        await Promise.all([
          this.getAssociateLifecycleEvents({
            associate_id: this.associate.id
          }),
          this.getAssociateCompanyFlow({
            associate_id: this.associate.id
          }),
          this.getSignedDocuments({
            associate_id: this.associate.id,
            size: 100
          }).catch(() => undefined)
        ])
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.profile-lifecycle-subtitle {
  color: var(--text-color-secondary);
}

.empty-flow {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color-secondary);
  border: 1px dashed var(--border-color-base);
  border-radius: 6px;
}
</style>
